// The required-document checklist for an OMK individual Last Expense claim.
//
// Driven by the BRD's "Required Documentation" section. Three of its rules depend
// on the claim rather than being fixed, and each was previously either hard-coded
// or missing:
//
//   - the deceased's identity document is a BIRTH CERTIFICATE for a child, not a
//     national ID they cannot have;
//   - an accidental death additionally requires a police report or postmortem
//     report;
//   - the death certificate is only mandatory once the payout exceeds the
//     KES 250,000 tranche threshold.
//
// Kept in lib/ rather than the partner app for the same reason as
// claimBenefitCaps: the customer-facing journey shows the same checklist and the
// two must not drift.
//
// No optional chaining / ES6+ syntax the project's Babel config rejects.

import { yearsBetween, SPLIT_PAYOUT_THRESHOLD } from './claimBenefitCaps.es6'

/** Below this age the deceased's identity document is the birth certificate. */
export const MINOR_AGE = 18

// The dates yearsBetween can actually read. Kept in step with its own parser so
// this module never asks it to measure to something it will quietly replace.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/

/** Marks a checklist row as required. The UI shows the asterisk verbatim. */
const REQUIRED = ' *'

/**
 * Is the deceased a child?
 *
 * Age is derived from the date of birth and the date of death when both are
 * present, because that is the only figure that is right for certain. A stored
 * `age` is used next. Relationship is the LAST resort and only consulted when no
 * age is available at all: a "Child" on a policy schedule can be forty years old,
 * and asking their estate for a birth certificate instead of an ID is just as
 * un-completable as the bug this replaces.
 *
 * @param {object} input
 *   dateOfBirth   yyyy-mm-dd
 *   dateOfDeath   yyyy-mm-dd
 *   age           age at death, if already computed and stored on the claim
 *   relationship  relationship to the principal
 */
export function deceasedIsMinor(input) {
  const opts = input || {}

  // Only derive when there really is a date of death to measure to, and test it the
  // same way yearsBetween does — it substitutes today for a `to` it cannot parse, so
  // a missing OR malformed date silently turns this into "how old would they be now".
  // For someone who died at 17 two years ago that reads 19 and asks their estate for
  // a national ID they never had, the exact bug this module exists to prevent.
  // Falling through leaves the stored age at death below to answer, which it can.
  const derived = ISO_DATE.test(String(opts.dateOfDeath || ''))
    ? yearsBetween(opts.dateOfBirth, opts.dateOfDeath)
    : null
  if (derived !== null) {
    return derived < MINOR_AGE
  }

  // Number() yields NaN for anything unparseable and NaN >= 0 is false, so that one
  // comparison rejects junk without reaching for isNaN. The emptiness checks stay
  // because Number(null) and Number('') are both 0, which would read as a newborn.
  const rawAge = opts.age
  const stored = Number(rawAge)
  const hasStoredAge =
    rawAge !== null && rawAge !== undefined && rawAge !== '' && stored >= 0
  if (hasStoredAge) {
    return stored < MINOR_AGE
  }

  return /\bchild\b|\bson\b|\bdaughter\b|minor/i.test(
    String(opts.relationship || '')
  )
}

/** Does this cause of death bring in the police / postmortem report? */
export function isAccidentalDeath(causeOfDeath) {
  return /accident/i.test(String(causeOfDeath || ''))
}

/**
 * The checklist for one claim.
 *
 * Every row carries `aliases`: the other labels the same document has been
 * uploaded under, here and on the customer journey. Matching on aliases is what
 * stops a relabelled requirement showing as "missing" against a file where the
 * document is already present — a claim that was uploaded a "Police report"
 * before this row became "Police report / postmortem report" still matches.
 *
 * @param {object} input
 *   ageAtDeath | dateOfBirth + dateOfDeath   the deceased's age (see deceasedIsMinor)
 *   relationship        relationship to the principal
 *   causeOfDeath        "Natural death" / "Accidental death"
 *   payableAmount       the payout in KES (already capped), drives the death certificate
 * @returns {{required: Array, optional: Array}}
 */
export function omkLastExpenseDocuments(input) {
  const opts = input || {}

  const isMinor = deceasedIsMinor({
    dateOfBirth: opts.dateOfBirth,
    dateOfDeath: opts.dateOfDeath,
    age: opts.ageAtDeath,
    relationship: opts.relationship
  })

  const required = [
    {
      description: `Burial permit${REQUIRED}`,
      aliases: [
        'burial permit',
        'certified copy of death certificate/ burial permit'
      ]
    },
    {
      description: `Principal member national ID / passport${REQUIRED}`,
      aliases: [
        'principal member national id / passport',
        'principal member national id',
        'id front',
        'id back',
        'id document',
        'id/passport'
      ]
    },
    isMinor
      ? {
          description: `Deceased birth certificate${REQUIRED}`,
          aliases: [
            'deceased birth certificate',
            'birth certificate',
            'birth certificate/marriage certificate'
          ]
        }
      : {
          description: `Deceased national ID / passport${REQUIRED}`,
          aliases: [
            'deceased national id / passport',
            'deceased id copy',
            'deceased national id'
          ]
        },
    {
      description: `Executed claim form${REQUIRED}`,
      aliases: ['executed claim form', 'claim form']
    }
  ]

  // BRD §5.10.3: the first KES 250,000 is paid on the base documents, the balance
  // only once the death certificate is in.
  //
  // `gatesBalanceOnly` is the whole point of the flag. This row was plain required,
  // and a missing required row blocks Finalise Documentation — so a KES 500,000 claim
  // with no certificate could not be finalised at all, and the first tranche that is
  // supposed to be payable within 48 hours WITHOUT the certificate could never be
  // released. The document is still mandatory, but for the balance, not for the file.
  if (Number(opts.payableAmount) > SPLIT_PAYOUT_THRESHOLD) {
    required.push({
      description: `Death certificate${REQUIRED}`,
      gatesBalanceOnly: true,
      aliases: ['death certificate', 'certified copy of death certificate']
    })
  }

  // An accidental death is not assessable on the base documents alone: either the
  // police report or the postmortem report must establish what happened. Either
  // document satisfies the row.
  if (isAccidentalDeath(opts.causeOfDeath)) {
    required.push({
      description: `Police report / postmortem report${REQUIRED}`,
      aliases: [
        'police report',
        'police abstract',
        'postmortem report',
        'post mortem report',
        'post-mortem report',
        'autopsy report',
        'autopsy/medical report'
      ]
    })
  }

  // ISS-042: the executed claim form already carries an equivalent section for the
  // hospital or the area chief to complete, so that letter is uploadable but never
  // required. Proof of payment is optional too.
  const optional = [
    {
      description: 'Hospital report / area chief letter',
      aliases: [
        'hospital report / area chief letter',
        'hospital report',
        'area chief letter'
      ]
    },
    { description: 'Proof of Payment', aliases: ['proof of payment'] }
  ]

  return { required, optional }
}

/** Drop the required-marker asterisk and case so two labels can be compared. */
export function normalizeDocumentLabel(value) {
  return String(value === null || value === undefined ? '' : value)
    .replace(/\*/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

/**
 * Has `doc` already been uploaded? Matches the row's own label or any of its
 * aliases against the descriptions on the claim.
 *
 * @param {Array} uploaded  the claim's documents
 * @param {object} doc      a checklist row from omkLastExpenseDocuments
 */
export function findUploadedDocument(uploaded, doc) {
  if (!Array.isArray(uploaded) || !doc) {
    return undefined
  }
  const wanted = [normalizeDocumentLabel(doc.description)].concat(
    (doc.aliases || []).map(normalizeDocumentLabel)
  )
  for (let i = 0; i < uploaded.length; i += 1) {
    const candidate = uploaded[i]
    const label = normalizeDocumentLabel(
      candidate &&
        (candidate.description || candidate.document_type || candidate.name)
    )
    if (label && wanted.indexOf(label) !== -1) {
      return candidate
    }
  }
  return undefined
}
